/**
 * Image Viewer Dialog Component
 *
 * Modal dialog for viewing photos and signatures from checklist items.
 */

import { useState } from 'react';
import { X, Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ImageViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string | null;
  title: string;
  type: 'photo' | 'signature';
}

export function ImageViewerDialog({
  open,
  onOpenChange,
  imageUrl,
  title,
  type,
}: ImageViewerDialogProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  const handleDownload = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  // Reset state when dialog opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLoading(true);
      setError(false);
      setZoom(1);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>{title}</DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[3rem] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleDownload}
              title="Open in new tab"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="relative flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto bg-muted/30 rounded-lg">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground p-8">
              <X className="h-12 w-12" />
              <p>Failed to load {type}</p>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                Try opening in new tab
              </Button>
            </div>
          ) : (
            imageUrl && (
              <img
                src={imageUrl}
                alt={title}
                onLoad={handleImageLoad}
                onError={handleImageError}
                className={`transition-transform duration-200 ${
                  type === 'signature' ? 'bg-white p-4 rounded' : ''
                }`}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center',
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  visibility: loading ? 'hidden' : 'visible',
                }}
              />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
